import {
  Alert,
  Details,
  Dialog,
  Heading,
  List,
  Paragraph,
} from "@digdir/designsystemet-react";
import { useTranslation } from "react-i18next";
import { addDaysToDate, formatDate } from "@/utils/dateTimeUtils";
import { useKjøreplaner } from "./useKjøreplaner";

// Fristen for å levere målerdata er kl. 12 dagen før oppgjøret kjøres
const FRIST_KLOKKESLETT = 12;

const tilInnrapporteringsFrist = (kjøredato: Date): Date => {
  const frist = addDaysToDate(kjøredato, -1);
  frist.setHours(FRIST_KLOKKESLETT, 0, 0, 0);
  return frist;
};

export const Kjøreplaner = () => {
  const { t } = useTranslation();
  const { kjøreplaner, isLoading, isError } = useKjøreplaner();

  const now = new Date();
  const inneværendeÅr = now.getFullYear();

  const fristerPerÅr = kjøreplaner
    .map(tilInnrapporteringsFrist)
    .reduce<Record<number, Date[]>>((acc, frist) => {
      const år = frist.getFullYear();
      acc[år] = [...(acc[år] ?? []), frist];
      return acc;
    }, {});

  const årstall = Object.keys(fristerPerÅr)
    .map(Number)
    .sort((a, b) => a - b);

  const getStyle = (date: Date) => {
    if (date < now) {
      return "text-gray-500 line-through";
    } else {
      return "text-black";
    }
  };

  return (
    <Dialog.TriggerContext>
      <Dialog.Trigger variant="secondary">
        <span>{t("pages.kjøreplaner.åpne-kjøreplaner-dialog")}</span>
      </Dialog.Trigger>
      <Dialog closedby="any">
        <Heading level={2} data-size="sm">
          {t("pages.kjøreplaner.tittel")}
        </Heading>
        <Paragraph>{t("pages.kjøreplaner.beskrivelse")}</Paragraph>
        <div className="mt-4">
          {isLoading ? (
            <Alert data-color="info">{t("generisk.laster")}</Alert>
          ) : isError ? (
            <Alert data-color="danger">{t("generisk.feil")}</Alert>
          ) : (
            årstall.map((år) => (
              <Details
                key={år}
                defaultOpen={år === inneværendeÅr}
                variant="default"
              >
                <Details.Summary>
                  <Heading level={3} data-size="xs">
                    {år}
                  </Heading>
                </Details.Summary>
                <Details.Content>
                  <List.Unordered>
                    {fristerPerÅr[år].map((frist) => (
                      <List.Item
                        key={frist.toString()}
                        className={getStyle(frist)}
                      >
                        {t("pages.kjøreplaner.frist", {
                          dato: formatDate(frist),
                          klokkeslett: FRIST_KLOKKESLETT,
                        })}
                      </List.Item>
                    ))}
                  </List.Unordered>
                </Details.Content>
              </Details>
            ))
          )}
        </div>
      </Dialog>
    </Dialog.TriggerContext>
  );
};
